import moment from "moment";

import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { CheckboxLabel } from "@/DesignSystem";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { IMedicalItemsModel } from "@/Patient/Models/UI/MedicalSummary/IMedicalItemsModel";
import { BaseMedicalItemsView, IDetailsExtraHeaderRenderFunction, IItemDetailsEditRenderFunction, IItemDetailsRenderFunction, IListItemRenderFunction, IMedicalItemsViewProps } from "./BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";
import { LifeStageRow } from "./LifeStageRow";

class MedicalItemAlertViewState extends BaseMedicalItemsViewState {
    public IsNewEMRTemplate: boolean;

    public AlertName: string;
    public AlertDescription: string;
    public EndDate: Date;
    public EndDateText: string;
    public EndDatePrecision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day;
    public IsEndDateApproximative: boolean;
    public AlertActivated: boolean;
    public Notes: string;
    public isVisibleOnExport: boolean;

    public Id: string;
    public DisplayId: string;

    constructor(s: MedicalItemAlertViewState = null) {
        super(s);

        if (s != null) {
            this.IsNewEMRTemplate = s.IsNewEMRTemplate;
            this.AlertName = s.AlertName;
            this.EndDatePrecision = s.EndDatePrecision;
            this.IsEndDateApproximative = s.IsEndDateApproximative;
            this.Notes = s.Notes;
            this.Id = s.Id;
            this.EndDate = s.EndDate;
            this.EndDateText = s.EndDateText;
            this.EndDatePrecision = s.EndDatePrecision;
            this.IsEndDateApproximative = s.IsEndDateApproximative;
            this.isVisibleOnExport = s.isVisibleOnExport;
        }
        else {
            this.AlertName = "";
            this.AlertActivated = true;
            this.isVisibleOnExport = true;
        }
    }
}

class MedicalItemsAlertView extends BaseMedicalItemsView<IMedicalItemsViewProps, MedicalItemAlertViewState> {
    public componentWillMount(): void {
        var s = null;
        let cacheId = this.getItemCacheId(this.props);
        if (cacheId != null) {
            s = patientStore.getMedicalItemCachedState(this.props.PatientContactId, cacheId);
        }
        if (s == null) {
            s = new MedicalItemAlertViewState();
        }
        this.setState(s);
    }

    public componentWillReceiveProps(nextProps: IMedicalItemsViewProps): void {
        // when the model changes, check if we have to render an item in edit mode, if so, we set the state so we can edit its values
        const model = nextProps.Model;

        if (model != this.props.Model) {
            const isEditing = this.isEditing(model);
            const medicalItems = model.get("MedicalItemsList")?.toArray() ?? [];

            if (isEditing && medicalItems && medicalItems.length > 0) {
                const latestNewItem = MedicalItemModel.sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id))).firstOrDefault();

                if (latestNewItem != null) {
                    // Use the new MedicalItem
                    this.SetEditedItemState(latestNewItem);
                }
                else {
                    // modify the last item if Uncommitted
                    const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

                    if (latestMedicalItem != null && !latestMedicalItem.IsCommitted)
                        this.SetEditedItemState(latestMedicalItem);
                }
            }
        }
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        $("#kenddate").parent().css("width", "96px").parent().css("width", "118px");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.Id, this.state);
    }

    public render(): JSX.Element {
        const self = this;
        const state = this.state;

        const listItemRenderFunction: IListItemRenderFunction = function(alert: MedicalItemAlertsAndSpecialNeeds): JSX.Element {
            const lifeStageString = PatientMedicalSummaryHelper.GetLifeStageString(alert);

            return (
                <div className="med-note" key={"alert" + alert.Id}>
                    <div className="flex-container medical-note">
                        <div className="flx-col a">
                            <p className="ellipsis bold">{alert.Name}</p>
                            {alert.EMRTemplateIsInvalid && <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")}</p>}
                            <p className="ellipsis"> {lifeStageString}</p>
                        </div>
                    </div>
                </div>
            );
        };

        const extraHeaderFunction: IDetailsExtraHeaderRenderFunction = function(alert: IMedicalItemsModel): JSX.Element {
            const result: JSX.Element[] = [];

            if (alert.get("MedicalItemsList")) {
                const alerts = alert.get("MedicalItemsList").toArray();

                if (cacheStore.ClinicSettings.EnableAlertsAndSpecialNeedsSettings) {
                    result.push(
                        <CheckboxLabel
                            disabled={!alerts[0]?.EMRTemplateIsVisibleInCPP}
                            checked={Boolean(alerts[0]?.EMRTemplateIsVisibleOnExport || (state.isVisibleOnExport && state.IsNewEMRTemplate))}
                            label={cacheStore.GetString("ShowInPrintedSummaryLabel")}
                            onChange={() => self.ToggleVisibilityOfSummaryAlertForPrinting(alerts[0].EMRTemplateId, alerts[0].EMRTemplateIsVisibleOnExport)}
                            dataCy="medicalItemsAlert-showInPrintedSummary"
                        />
                    );
                }

                if (alerts.length > 0) {
                    result.push(
                        <CheckboxLabel
                            checked={Boolean(alerts[0].EMRTemplateIsDisplayPopup || (state.AlertActivated && state.IsNewEMRTemplate))}
                            label={cacheStore.GetString("ShowPopupWhenOpeningPatientFileLabel")}
                            onChange={() => self.ToggleAlertActivation(alerts[0].EMRTemplateId, alerts[0].EMRTemplateIsDisplayPopup)}
                            dataCy="medicalItemsAlert-activation"
                        />
                    );
                }
            }

            return <>{result}</>;
        };

        let itemDetailsRenderFunction: IItemDetailsRenderFunction = function(alert: MedicalItemAlertsAndSpecialNeeds): JSX.Element {
            let lifeStageString = PatientMedicalSummaryHelper.GetLifeStageString(alert);

            let endDateDisplay = alert.EndDate != null ? PatientMedicalItemsHelper.GetLifeStageDateString(alert.EndDate, alert.PrecisionEndDate) : "";
            if (alert.PrecisionEndDate == LifeStageDatePrecisionEnum.DayApprox && endDateDisplay != null && endDateDisplay.length > 0)
                endDateDisplay = "≈ " + endDateDisplay;

            return (
                <div key={"alert" + alert.Id} className="medical-reaction-detail-box">
                    <table className="medical-reaction-details border" data-cy="medicalItemsAlert-details">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("AlertNameDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(alert.Name)} </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("AlertDescriptionDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(alert.Description))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("StartDateLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong data-cy="medicalItemsAlert-startDate">{lifeStageString}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("EndDateDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{endDateDisplay}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(alert.Notes))}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        let itemDetailsEditRenderFunction: IItemDetailsEditRenderFunction = function(alert: MedicalItemAlertsAndSpecialNeeds): JSX.Element {
            var endDateApproxDisabled = "";
            if (state != null)
                endDateApproxDisabled = state.EndDatePrecision == LifeStageDatePrecisionEnum.MonthYear ||
                    state.EndDatePrecision == LifeStageDatePrecisionEnum.Year ? "disabled" : "";

            return (
                <div className="medical-reaction-detail-box">
                    <table className="medical-problems-edit border">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("AlertNameDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextboxControl
                                        id="__dfbf98y4343"
                                        onMountFocus={true}
                                        valid={state.AlertName != null && state.AlertName.trim().length > 0}
                                        value={state.AlertName}
                                        onChange={value => self.AlertNameChanged(value)}
                                        className="form-control"
                                        dataCy="medicalItemsAlert-name"
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("AlertDescriptionDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"alert_desc" + (alert.displayId ? alert.displayId : alert.MedicalItemId)}
                                        text={state.AlertDescription}
                                        isDisabled={false}
                                        onChange={value => self.AlertDescriptionChanged(value)}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                        verticalResizeOnly={true}
                                        dataCy="medicalItemsAlert-description"
                                    />
                                </td>
                            </tr>

                            <LifeStageRow
                                lifeStageMode={self.state.LifeStageMode}
                                lifeStageAvailableAgeAtOnsetUnits={self.state.LifeStageAvailableAgeAtOnsetUnits}
                                lifeStageAgeSelectedAgeAtOnsetUnit={self.state.LifeStageAgeSelectedgeAtOnsetUnit}
                                lifeStageSelectedLifeStageLookupId={self.state.LifeStageSelectedLifestageLookupId}
                                lifeStageAvailableLifeStages={self.state.LifeStageAvailableLifestages}
                                lifeStageAgeAtOnset={self.state.LifeStageAgeAtOnset}
                                lifeStageDatePrecision={self.state.LifeStageDatePrecision}
                                lifeStageDateTextboxValue={self.state.LifeStageDateTextboxValue}
                                lifeStageStartDate={self.state.LifeStageStartDate}
                                lifeStageIsStartDateApprox={self.state.LifeStageIsStartDateApproximative}
                                useThreeCols={false}
                                onLifeStageChange={value => self.LifeStageChanged(value)}
                                onLifeStageDateChange={(date, precision) => self.LifeStageDateChanged(date, precision)}
                                onLifeStageModeClick={newMode => self.LifeStageModeChanged(newMode)}
                                onLifeStageDateApproxChange={() => self.LifeStageDateApproxChange()}
                                onLifeStageDateTodayClick={() => self.LifeStageDateTodayClicked()}
                                onLifeStageAgeChange={value => self.LifeStageAgeChange(value)}
                                onLifeStageAgeUnitChange={value => self.LifeStageAgeUnitChange(value)}
                            />

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("EndDateDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControlWithDatePicker
                                            id="resolutionDP"
                                            textValue={self.state.EndDateText}
                                            dateValue={state.EndDate}
                                            onChange={(datePrecision: DateTimeWithPrecision) => {
                                                self.EndDateChanged(datePrecision, datePrecision.precision);
                                            }}
                                            dataCy="medicalItemsAlert-endDate"
                                        />

                                        <div style={{ paddingLeft: "10px", marginTop: "5px" }} onClick={e => self.EndDateApproxChanged(e)}>
                                            <input disabled={endDateApproxDisabled === "disabled"} type="checkbox" onChange={e => self.EndDateApproxChanged(e)} checked={state.IsEndDateApproximative} />
                                            <span>Approx.</span>
                                        </div>
                                        <div style={{ paddingLeft: "10px", marginTop: "5px", cursor: "pointer" }}>
                                            <a style={{ cursor: "pointer" }} onClick={() => self.EndDateTodayClicked()}>
                                                <span>
                                                    {cacheStore.GetString("TodayLabel")}
                                                </span>
                                            </a>
                                        </div>
                                    </div>
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"exposure_Det" + (alert.displayId ? alert.displayId : alert.MedicalItemId)}
                                        text={state.Notes}
                                        isDisabled={false}
                                        onChange={value => self.NotesChanged(value)}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                        verticalResizeOnly={true}
                                        dataCy="medicalItemsAlert-notes"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        return (
            <BaseMedicalItemsView
                PatientContactId={this.props.PatientContactId}
                Model={this.props.Model}
                DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("AlertsAndSpecialNeedsMinLabel")}
                ListItemRenderFunction={listItemRenderFunction}
                MedicalItemRenderFunction={itemDetailsRenderFunction}
                DetailsExtraHeaderRenderFunction={extraHeaderFunction}
                MedicalItemEditRenderFunction={itemDetailsEditRenderFunction}
                onShowInMedicalSummaryClick={checked => this.onShowInMedicalSummaryClick(checked)}
                SaveFunction={() => this.Save()}
                SavePlusFunction={() => this.Save(true)}
                emrType={MedicalItemTypeEnum.AlertsAndSpecialNeeds}
                CheckIfSaveEnabledFunction={() => self.CheckIfSaveIsEnabled()}
                toSearchableStringFunction={(items: MedicalItem[]) => self.searchMedicalItem(items)}
                showFavorites={this.props.showFavorites}
            />
        );
    }

    public onShowInMedicalSummaryClick(checked: boolean): void {
        if (!checked) {
            this.mutateState(p => p.isVisibleOnExport = checked);
        }
    }

    public ToggleVisibilityOfSummaryAlertForPrinting(emrTemplateId: string, isVisibleOnExport: boolean): void {
        if (this.state.IsNewEMRTemplate) {
            this.mutateState(p => p.isVisibleOnExport = !p.isVisibleOnExport);
        }
        else {
            sessionStore.dispatch(LocalPatientOperations.MedicalSummary.ToggleVisibilityOfSummaryAlertForPrinting, {
                emrTemplateId: emrTemplateId,
                isVisibleOnExport: !isVisibleOnExport
            });
        }
    }

    public ToggleAlertActivation(emrTemplateId: string, isCurrentlyDisplayed: boolean): void {
        if (this.state.IsNewEMRTemplate) {
            // creating  : send activation togle diectly to backend
            var s = new MedicalItemAlertViewState(this.state);
            s.AlertActivated = !this.state.AlertActivated;
            this.setState(s);
        }
        else {
            // editing  : send activation togle diectly to backend
            sessionStore.dispatch(LocalPatientOperations.MedicalSummary.ToggleAlert, {
                emrTemplateId: emrTemplateId,
                newValue: !isCurrentlyDisplayed
            });
        }
    }

    private SetEditedItemState(medItem: MedicalItem): void {
        let item = medItem as MedicalItemAlertsAndSpecialNeeds;
        if (!item || item.IsCollapsed || item.IsCommitted)
            return;

        let isSameItem = item.displayId == this.state.DisplayId && item.Id == this.state.Id;
        if (isSameItem)
            return;

        var s = new MedicalItemAlertViewState(this.state);

        s.Id = item.Id;
        s.DisplayId = item.displayId;

        if (item.IsNewEMRTemplate) {
            s.AlertName = "";
            s.AlertDescription = "";
            s.Notes = "";
            s.EndDate = null;
            s.EndDateText = "";
            s.AlertActivated = cacheStore.ClinicSettings.EnableAlertsAndSpecialNeedsSettings ? settingsStore.configuration.get("ShowPopupForNewAlertsAndSpecialNeeds") : true;
            s.IsEndDateApproximative = false;
            s.isVisibleOnExport = cacheStore.ClinicSettings.EnableAlertsAndSpecialNeedsSettings ? settingsStore.configuration.get("ShowAlertsAndSpecialNeedsInPrintedSummary") : true;
        }
        else {
            s.AlertName = item.Name;
            s.AlertDescription = item.Description;
            s.IsNewEMRTemplate = item.IsNewEMRTemplate;
            s.Notes = item.Notes;
            s.EndDate = item.EndDate;
            s.EndDatePrecision = item.PrecisionEndDate;
            s.EndDateText = PatientMedicalItemsHelper.GetLifeStageDateString(s.EndDate, s.EndDatePrecision);
            s.IsEndDateApproximative = !(item.PrecisionEndDate == LifeStageDatePrecisionEnum.Day);

            s.AlertActivated = item.EMRTemplateIsDisplayPopup;
            s.isVisibleOnExport = item.EMRTemplateIsVisibleOnExport;
        }

        s.IsNewEMRTemplate = item.IsNewEMRTemplate;
        s.LifeStageStartDate = item.LifeStageDate;
        s.LifeStageIsStartDateApproximative = !(item.LifeStagePrecisionDate == LifeStageDatePrecisionEnum.Day);
        s.LifeStageDateTextboxValue = s.LifeStageStartDate != null ? PatientMedicalItemsHelper.GetLifeStageDateString(s.LifeStageStartDate, item.LifeStagePrecisionDate) : "";
        s.LifeStageDatePrecision = item.LifeStagePrecisionDate;
        s.LifeStageAgeAtOnset = item.LifeStageAge;
        s.LifeStageMode = item.LifeStageMode != null ? item.LifeStageMode : s.LifeStageMode;
        s.LifeStageSelectedLifestageLookupId = item.LifeStageStatusLookupId != null ? item.LifeStageStatusLookupId : s.LifeStageSelectedLifestageLookupId;
        s.LifeStageAgeSelectedgeAtOnsetUnit = item.LifeStageAgeStatusLookupId != null ? item.LifeStageAgeStatusLookupId : s.LifeStageAgeSelectedgeAtOnsetUnit;

        this.setState(s);
    }

    private EndDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemAlertViewState(this.state);

        if (date.value) {
            s.EndDate = moment(date.value).toDate();
            s.EndDatePrecision = precision;

            if (date.isFromDateSelector) {
                s.EndDateText = moment(date.value).format("YYYY-MM-DD");
            }
            else if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.EndDateText = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
                s.IsEndDateApproximative = false;

                if (s.LifeStageIsStartDateApproximative === false)
                    s.EndDatePrecision = LifeStageDatePrecisionEnum.Day;
            }
            else if (precision == LifeStageDatePrecisionEnum.MonthYear || precision == LifeStageDatePrecisionEnum.Year) {
                s.EndDateText = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.IsEndDateApproximative = true;
            }
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.EndDate = null;
            s.EndDateText = "";
            s.IsEndDateApproximative = false;
        }

        this.setState(s);
    }

    private EndDateApproxChanged(e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLDivElement>): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.IsEndDateApproximative = e.target.checked;
        s.EndDatePrecision = e.target.checked ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;
        this.setState(s);
    }

    private EndDateTodayClicked(): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.EndDateText = moment(Date.now()).format("YYYY-MM-DD");
        s.EndDate = moment(Date.now()).toDate();
        s.EndDatePrecision = (this.state.IsEndDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private AlertNameChanged(text: string): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.AlertName = text;
        this.setState(s);
    }

    private AlertDescriptionChanged(value: string): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.AlertDescription = value;
        this.setState(s);
    }

    private NotesChanged(value: string): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.Notes = value;
        this.setState(s);
    }

    private LifeStageModeChanged(newMode: LifeStageModeEnum): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.LifeStageMode = newMode;

        if (s.LifeStageMode == LifeStageModeEnum.Age) {
            // s.LifeStageDatePrecision = LifeStageDatePrecisionEnum.MonthYear;
            s.LifeStageAgeSelectedgeAtOnsetUnit = cacheStore.FindLookupByLookupCode("year", "DayYear").LookupID;
            s.LifeStageAgeAtOnset = undefined;
            s.LifeStageIsStartDateApproximative = false;
        }
        else if (s.LifeStageMode == LifeStageModeEnum.Date) {
            s.LifeStageIsStartDateApproximative = s.LifeStageDatePrecision != LifeStageDatePrecisionEnum.Day;
        }

        this.setState(s);
    }

    private LifeStageDateApproxChange(): void {
        if (this.state && this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.MonthYear || this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.Year)
            return;

        var s = new MedicalItemAlertViewState(this.state);
        s.LifeStageIsStartDateApproximative = !s.LifeStageIsStartDateApproximative;
        s.LifeStageDatePrecision = s.LifeStageIsStartDateApproximative ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemAlertViewState(this.state);

        if (date.value) {
            s.LifeStageStartDate = moment(date.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
                    LifeStageDatePrecisionEnum.DayApprox :
                    LifeStageDatePrecisionEnum.Day;

                s.LifeStageDateTextboxValue = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
            }
            else {
                s.LifeStageDatePrecision = precision;
                s.LifeStageDateTextboxValue = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.LifeStageIsStartDateApproximative = true;
            }

            s.LifeStageMode = LifeStageModeEnum.Date;
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.LifeStageStartDate = null;
            s.LifeStageDateTextboxValue = "";
            s.LifeStageIsStartDateApproximative = false;
        }

        this.setState(s);
    }

    private LifeStageDateTodayClicked(): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.LifeStageDateTextboxValue = moment(Date.now()).format("YYYY-MM-DD");
        s.LifeStageStartDate = moment(Date.now()).toDate();
        s.LifeStageMode = LifeStageModeEnum.Date;
        s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;
        this.setState(s);
    }

    private LifeStageChanged(lookupId: string): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.LifeStageSelectedLifestageLookupId = lookupId;
        s.LifeStageMode = LifeStageModeEnum.LifeStage;
        this.setState(s);
    }

    private LifeStageAgeChange(value: string): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.LifeStageAgeAtOnset = PatientMedicalItemsHelper.parseLifeStageAgeAtOnsetValue(value);
        s.LifeStageMode = LifeStageModeEnum.Age;

        this.setState(s);
    }

    private LifeStageAgeUnitChange(lookupId: string): void {
        var s = new MedicalItemAlertViewState(this.state);
        s.LifeStageAgeSelectedgeAtOnsetUnit = lookupId;
        s.LifeStageMode = LifeStageModeEnum.Age;
        this.setState(s);
    }

    private async Save(isSavePlus: boolean = false): Promise<void> {
        let medicalItems = this.props.Model.get("MedicalItemsList").toArray();
        let editMedicalItem = medicalItems.firstOrDefault(x => x.IsCommitted == false) as MedicalItemAlertsAndSpecialNeeds;
        editMedicalItem = MyleHelper.CloneObject(editMedicalItem);

        let medicalItemsToSend: MedicalItemAlertsAndSpecialNeeds[] = [];

        if (editMedicalItem != null) {
            if (editMedicalItem.IsNewEMRTemplate) {
                editMedicalItem.EMRTemplateIsDisplayPopup = this.state.AlertActivated;
                editMedicalItem.EMRTemplateIsVisibleOnExport = this.state.isVisibleOnExport;
            }

            editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(this.state.LifeStageStartDate);
            editMedicalItem.LifeStageMode = this.state.LifeStageMode;
            editMedicalItem.LifeStagePrecisionDate = this.state.LifeStageDatePrecision;

            // note: SL has an ugly design where it bases itself on the birthdate on a patient to deal with age on offset and lifestage to create a date
            //       so we don't add lifestage if age@onset/lifestage selected and patient has no birthdate
            if (this.state.LifeStageSelectedLifestageLookupId || (this.state.LifeStageAgeAtOnset && patientStore.PatientModels[this.props.PatientContactId].IndividualResponse.DateOfBirth)) {
                editMedicalItem.LifeStageAge = this.state.LifeStageAgeAtOnset;
                editMedicalItem.LifeStageAgeStatusLookupId = this.state.LifeStageAgeSelectedgeAtOnsetUnit;
                editMedicalItem.LifeStageStatusLookupId = this.state.LifeStageSelectedLifestageLookupId;

                // deal with age of onset
                if (editMedicalItem.LifeStageMode == LifeStageModeEnum.Age) {
                    editMedicalItem.LifeStagePrecisionDate == LifeStageDatePrecisionEnum.Day;
                    editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(moment(patientStore.PatientModels[this.props.PatientContactId].IndividualResponse.DateOfBirth).add(this.state.LifeStageAgeAtOnset, "years").toDate());
                }
                // deal with lifestage
                if (editMedicalItem.LifeStageMode == LifeStageModeEnum.LifeStage) {
                    editMedicalItem.LifeStagePrecisionDate == LifeStageDatePrecisionEnum.Day;

                    if (editMedicalItem.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("NewBorn", "LIFESTAGE").LookupID) {
                        editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(moment(patientStore.PatientModels[this.props.PatientContactId].IndividualResponse.DateOfBirth).toDate());
                    }
                    if (editMedicalItem.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Infant", "LIFESTAGE").LookupID) {
                        editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(moment(patientStore.PatientModels[this.props.PatientContactId].IndividualResponse.DateOfBirth).add(1, "months").toDate());
                    }
                    if (editMedicalItem.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Child", "LIFESTAGE").LookupID) {
                        editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(moment(patientStore.PatientModels[this.props.PatientContactId].IndividualResponse.DateOfBirth).add(2, "years").toDate());
                    }
                    if (editMedicalItem.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Adolescent", "LIFESTAGE").LookupID) {
                        editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(moment(patientStore.PatientModels[this.props.PatientContactId].IndividualResponse.DateOfBirth).add(16, "years").toDate());
                    }
                    if (editMedicalItem.LifeStageStatusLookupId == cacheStore.FindLookupByLookupCode("Adult", "LIFESTAGE").LookupID) {
                        editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(moment(patientStore.PatientModels[this.props.PatientContactId].IndividualResponse.DateOfBirth).add(18, "years").toDate());
                    }
                }
            }
            editMedicalItem.Name = this.state.AlertName;
            editMedicalItem.Description = this.state.AlertDescription;
            editMedicalItem.EndDate = cacheStore.Helper.CreateDateAsUTC(this.state.EndDate);
            editMedicalItem.PrecisionEndDate = this.state.EndDatePrecision;
            editMedicalItem.Notes = this.state.Notes;

            medicalItemsToSend.push(editMedicalItem);
        }

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter(medicalItemsToSend);
        }

        sessionStore.dispatch(LocalPatientOperations.MedicalItems.Save, {
            view: MedicalItemsTab.Alerts,
            editedItems: medicalItemsToSend,
            isSavePlus: isSavePlus
        });
    }

    private CheckIfSaveIsEnabled(): boolean {
        var isSaveEnabled = false;

        isSaveEnabled = this.state.AlertName != null &&
            this.state.AlertName.length > 0;

        return isSaveEnabled;
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemAlertsAndSpecialNeeds[] {
        const medicalitems: MedicalItemAlertsAndSpecialNeeds[] = items as MedicalItemAlertsAndSpecialNeeds[]; // cast MedicalItem to alerts

        return medicalitems.filterSearch(this.props.Model.get("SearchTerm"), item => {
            // items cannot be null for search, replace them with empty string
            const name = MyleHelper.replaceNullWithEmptyString(item.Name);
            const startDate = MyleHelper.replaceNullWithEmptyString(item.LifeStageDate);

            return name + " " + startDate;
        });
    }
}

export { MedicalItemsAlertView };
